// How an exam is set and how it is sat, step by step (byj-1wv.51.12), as a
// template's publishing is (pages/Templates' PublishSteps): numbered steps,
// each what it does and, where it is run, its commands to copy — folded
// under it where the steps are read through rather than followed. The
// sitting steps are on the page a student sits on, folded under its bar;
// behind "How exams work" — a dialog over the page, as its other short reads
// are (Confirm, a new class) — on the instructor's pages, whose own content
// comes first, setting and sitting as two tabs. The guide's Exams section
// (/docs#exams) says the rest.
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { ClassSummary } from "../api";
import type { ExamStage } from "../exams";
import { t, word, phrases } from "../i18n";
import { CommandLine } from "./CommandLine";
import { useModalKeys } from "./Modal";
import { Link } from "./Nav";
import { Steps } from "./Steps";
import { panelId, tabId, Tabs } from "./Tabs";

const stageIndex: Record<ExamStage, number> = { write: 0, check: 1, fix: 1, assign: 2, grade: 3 };

/**
 * The one thing to do next on an exam's page (byj-1wv.51.12): where it
 * stands among setting it — written, checked on the machine, assigned,
 * graded — and what this step asks (`text`, the stage's unless the page
 * knows better), with its commands to copy or what acts on it here
 * (`children`).
 */
export function NextStep({ stage, text, children }: { stage: ExamStage; text?: string; children?: ReactNode }) {
  const at = stageIndex[stage];
  return (
    <section className="card next-step" aria-labelledby="next-step-title" data-testid="next-step" data-stage={stage}>
      <ol className="stage-steps" aria-label={t("exams:nextStep")}>
        {phrases.stageSteps().map((s, i) => (
          <li key={s} className={i < at ? "is-done" : i === at ? "is-now" : ""} aria-current={i === at ? "step" : undefined}>
            <span className="stage-mark" aria-hidden="true">
              {i < at ? "✓" : i + 1}
            </span>
            {s}
          </li>
        ))}
      </ol>
      <div className="stack next-step-body">
        <h2 id="next-step-title" className="flush">
          {t("exams:nextStep")}: <span className="next-step-what">{text ?? word("exams:stageText", stage)}</span>
        </h2>
        {children}
      </div>
    </section>
  );
}

/**
 * How an exam is assigned, as the page can say it (byj-1wv.51.12): the
 * command for each class the reader teaches, its code in it; none, that a
 * class is made first. A paper of the item bank (`paper`, RFC-056) is not
 * published from the machine, whose versions the relay refuses it: it is
 * assigned as it is, version and all, and only to a class it was drawn for;
 * none of those taught, that the bank is asked for another (byj-1wv.96).
 */
export function AssignHow({ classes, paper }: { classes: ClassSummary[] | null; paper?: { exam: string; version: number; allowed: string[] } }) {
  if (classes === null) return null;
  const teaching = classes.filter((c) => c.role === "instructor" && c.join_code && (!paper || paper.allowed.includes(c.id)));
  if (teaching.length === 0) {
    return paper ? (
      <p className="muted small flush">
        {t("exams:assignPaperNone")} <Link to="/bank/request">{t("exams:assignPaperAsk")}</Link>
      </p>
    ) : (
      <p className="muted small flush">
        {t("exams:noTeachingClass")} <Link to="/classes">{t("classes:classesTitle")}</Link>
      </p>
    );
  }
  return (
    <div className="stack assign-how" data-testid="assign-how">
      {teaching.map((c) => (
        <div key={c.id} className="stack">
          <span className="small muted">{t("exams:assignTo", { cls: c.name })}</span>
          <CommandLine
            command={paper ? `byj-share exam assign ${paper.exam} --version ${paper.version} --class ${c.join_code}` : `byj exam publish KIT --class ${c.join_code}`}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * "How exams work": a button that opens how an exam is set in a dialog —
 * and, given `sitting`, how one is sat, the two as tabs — with the class's
 * code in the command that assigns one when the page knows it. Esc, its ✕
 * or a press outside closes it, the focus going back to the button.
 */
export function ExamGuideButton({ sitting = false, code = "CODE", className }: { sitting?: boolean; code?: string; className?: string }) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  return (
    <>
      <button ref={button} className={className} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? "exam-guide" : undefined} onClick={() => setOpen(true)}>
        {t("exams:examGuide")}
      </button>
      {open && <ExamGuideDialog sitting={sitting} code={code} onClose={close} />}
    </>
  );
}

type GuideTab = "setting" | "sitting";

function ExamGuideDialog({ sitting, code, onClose }: { sitting: boolean; code: string; onClose: () => void }) {
  const keys = useModalKeys(onClose);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [tab, setTab] = useState<GuideTab>("setting");
  useEffect(() => {
    closeButton.current?.focus();
  }, []);
  // a panel keeps the UA's display, so its hidden hides it: what lays it out is inside it
  const panel = (id: GuideTab) => (sitting ? { role: "tabpanel", id: panelId("guide", id), "aria-labelledby": tabId("guide", id), hidden: tab !== id } : {});
  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div id="exam-guide" className="modal guide-modal stack" role="dialog" aria-modal="true" aria-labelledby="exam-guide-title" onKeyDown={keys}>
        <div className="row">
          <h3 id="exam-guide-title" className="grow flush">
            {t("exams:examGuide")}
          </h3>
          <button ref={closeButton} type="button" className="small" aria-label={t("common:close")} onClick={onClose}>
            ✕
          </button>
        </div>
        {sitting && (
          <Tabs
            prefix="guide"
            label={t("exams:guideTabs")}
            value={tab}
            onChange={setTab}
            tabs={[
              { id: "setting", label: t("exams:examGuideSetting") },
              { id: "sitting", label: t("exams:examGuideSitting") },
            ]}
          />
        )}
        <div {...panel("setting")}>
          <div className="stack">
            <Steps steps={phrases.examSetSteps(code)} testid="exam-set-steps" fold />
            <p className="muted small flush">{t("exams:examGuideNames")}</p>
          </div>
        </div>
        {sitting && (
          <div {...panel("sitting")}>
            <Steps steps={phrases.examSitSteps("ASSIGNMENT", "KIND")} testid="exam-sit-steps" fold />
          </div>
        )}
        <div className="row modal-actions">
          <Link to="/docs#exams" className="button">
            {t("exams:examGuideMore")}
          </Link>
        </div>
      </div>
    </div>
  );
}
